import { create } from 'zustand';
import { Storage } from '@storage';

// Default settings values
export const DEFAULT_SETTINGS = {
  // Audio
  sound_enabled: true,
  music_volume: 33,
  sfx_volume: 100,
  // Display
  animation_speed: 'normal', // 'slow' | 'normal' | 'fast' | 'skip'
  show_damage_numbers: true,
  show_tooltips: true,
  locale: 'en', // 'en' | 'es' | 'pt'
  // Gameplay
  auto_end_turn: false,
  confirm_end_turn: true,
  // Theme
  theme: 'base', // theme slug: 'base' | 'dark' | etc.
  baseTheme: null, // slug of theme with isBase: true (for fallback lookup)
  // Keyboard shortcuts - maps key codes to semantic actions
  // Supports modifiers: "Alt+Escape", "Ctrl+KeyS", "Ctrl+Shift+KeyZ"
  shortcuts: {
    'Escape': 'cancel',
    'Enter': 'confirm',
    'Alt+Escape': 'toggleNextDrawer',
    // Future shortcuts:
    // 'Space': 'endTurn',
    // 'Digit1': 'action1',
    // 'Digit2': 'action2',
  },
};

// Valid tabs for the config modal
export const CONFIG_TABS = ['audio', 'display', 'gameplay', 'theme'];

// Debounce save to storage
let saveTimeout = null;
const debouncedSave = (settings) => {
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    Storage.set('config', 'settings', settings);
  }, 300);
};

const useConfigStore = create((set, get) => ({
  // Modal state
  isOpen: false,
  activeTab: 'audio',

  // Settings (synced with backend and IndexedDB)
  settings: { ...DEFAULT_SETTINGS },

  // Flag to track if loaded from cache
  _loadedFromCache: false,

  // Open modal, optionally on a specific tab
  openModal: (tab = 'audio') => {
    const validTab = CONFIG_TABS.includes(tab) ? tab : 'audio';
    set({ isOpen: true, activeTab: validTab });
  },

  // Close modal
  closeModal: () => {
    set({ isOpen: false });
  },

  // Change active tab
  setTab: (tab) => {
    if (CONFIG_TABS.includes(tab)) {
      set({ activeTab: tab });
    }
  },

  // Update a single setting (triggers auto-save to IndexedDB)
  updateSetting: (key, value) => {
    set((state) => {
      const newSettings = { ...state.settings, [key]: value };
      debouncedSave(newSettings);
      return { settings: newSettings };
    });
  },

  // Load settings from user.properties.config (from backend after login)
  loadSettings: (config) => {
    if (!config || typeof config !== 'object') {
      set({ settings: { ...DEFAULT_SETTINGS } });
      return;
    }

    // Deep merge for nested objects like shortcuts (new defaults + user overrides)
    const newSettings = {
      ...DEFAULT_SETTINGS,
      ...config,
      shortcuts: { ...DEFAULT_SETTINGS.shortcuts, ...(config.shortcuts || {}) },
    };
    set({ settings: newSettings });
    // Also save to storage
    Storage.set('config', 'settings', newSettings);
  },

  // Load settings from storage (called on app init, before login)
  init: async () => {
    if (get()._loadedFromCache) return;

    const cached = await Storage.get('config', 'settings');
    if (cached) {
      console.log('[⚙️ config] Restored from cache');
      // Deep merge for nested objects like shortcuts (new defaults + user overrides)
      const mergedSettings = {
        ...DEFAULT_SETTINGS,
        ...cached,
        shortcuts: { ...DEFAULT_SETTINGS.shortcuts, ...(cached.shortcuts || {}) },
      };
      set({
        settings: mergedSettings,
        _loadedFromCache: true,
      });
    } else {
      console.log('[⚙️ config] Using defaults');
      set({ _loadedFromCache: true });
    }
  },

  // Reset to default values
  resetToDefaults: () => {
    set({ settings: { ...DEFAULT_SETTINGS } });
    Storage.set('config', 'settings', DEFAULT_SETTINGS);
  },

  // Get current settings object (for saving to API)
  getSettingsForSave: () => {
    return { ...get().settings };
  },

  // Sync local settings to backend if they differ (called after login)
  // Returns true if sync was needed, false otherwise
  syncToBackendIfNeeded: async (backendConfig, saveToBackendFn) => {
    const localSettings = get().settings;
    const mergedBackend = { ...DEFAULT_SETTINGS, ...backendConfig };

    // Compare local vs backend
    const hasLocalChanges = JSON.stringify(localSettings) !== JSON.stringify(mergedBackend);

    if (hasLocalChanges) {
      console.debug('[ConfigStore] Local settings differ from backend, syncing...');
      try {
        await saveToBackendFn(localSettings);
        console.debug('[ConfigStore] Settings synced to backend');
        return true;
      } catch (error) {
        console.warn('[ConfigStore] Failed to sync settings to backend:', error.message);
        return false;
      }
    }

    return false;
  },
}));

export default useConfigStore;
